import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../config/paths';
import { useCallback, useEffect, useState } from 'react';

interface CardDelivery {
    user: {
        id: string;
        email: string;
        fujiyama_telegram_id: string | null;
        fujiyama_referral_code: string | null;
        country: string | null;
        postal_code: string | null;
        address1: string | null;
        address2: string | null;
        destination: string | null;
    };
    activity: {
        id: string;
        status: string;
        comment?: string | null;
        debit_card_category: string | null;
    };
}

interface CardDeliveriesResponse {
    deliveries: CardDelivery[];
    total: number;
    page: number;
    hasMore: boolean;
}

const useCardDeliveries = (page: number) => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CardDeliveriesResponse | null>(null);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    const fetchDeliveries = useCallback(async () => {
        if (!user || !accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/admin/debit-card-delivery?page=${page}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('リクエストの取得に失敗しました');
            }

            const responseData: CardDeliveriesResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken, page]);

    const markAsDelivered = useCallback(async (activityId: string, deliveryComment: string) => {
        if (!user || !accessToken) return;
        if (!deliveryComment.trim()) {
            setError('コメントを入力してください');
            return;
        }

        setProcessingIds(prev => new Set(prev).add(activityId));
        try {
            const response = await fetch('/api/v1/admin/debit-card-delivery', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: activityId,
                    comment: deliveryComment
                })
            });

            if (!response.ok) {
                throw new Error('発送済み処理に失敗しました');
            }

            // ローカルでデータを更新
            setData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    deliveries: prev.deliveries.map(delivery =>
                        delivery.activity.id === activityId
                            ? {
                                ...delivery,
                                activity: {
                                    ...delivery.activity,
                                    status: 'processed',
                                    comment: deliveryComment
                                }
                            }
                            : delivery
                    )
                };
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(activityId);
                return next;
            });
        }
    }, [user, accessToken]);

    useEffect(() => {
        fetchDeliveries();
    }, [fetchDeliveries]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchDeliveries,
        markAsDelivered,
        processingIds
    };
};

export const CardDeliveryPage = () => {
    const [user] = useAtom(userAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const [comments, setComments] = useState<Map<string, string>>(new Map());
    const {
        data,
        isLoading,
        error,
        markAsDelivered,
        processingIds
    } = useCardDeliveries(currentPage);

    const handleCommentChange = (activityId: string, value: string) => {
        setComments(prev => new Map(prev).set(activityId, value));
    };

    if (!user?.has_admin_role && !user?.has_card_delivery_check_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (data?.hasMore) {
            setCurrentPage(prev => prev + 1);
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">FujiyamaTap Debit配送管理</h1>
            <div className="bg-white shadow rounded-lg p-6">
                <div className="space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ユーザー情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ステータス
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        カード種別
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        配送先住所
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        カード種別
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        アクション
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : data?.deliveries.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                            配送リクエストが見つかりませんでした
                                        </td>
                                    </tr>
                                ) : (
                                    data?.deliveries.map((delivery) => (
                                        <tr key={delivery.activity.id}>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="font-medium text-gray-900">{delivery.user.email}</div>
                                                    {delivery.user.fujiyama_telegram_id && (
                                                        <div className="text-gray-500">
                                                            Telegram ID: {delivery.user.fujiyama_telegram_id}
                                                        </div>
                                                    )}
                                                    {delivery.user.fujiyama_referral_code && (
                                                        <div className="text-gray-500">
                                                            Fujiyama Referral Code: {delivery.user.fujiyama_referral_code}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${delivery.activity.status === 'waiting'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : delivery.activity.status === 'processed'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {delivery.activity.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">{delivery.activity.debit_card_category}</td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {delivery.user.country && (
                                                        <div>国: {delivery.user.country}</div>
                                                    )}
                                                    {delivery.user.postal_code && (
                                                        <div>郵便番号: {delivery.user.postal_code}</div>
                                                    )}
                                                    {delivery.user.address1 && (
                                                        <div className="truncate max-w-xs">
                                                            住所1: {delivery.user.address1}
                                                        </div>
                                                    )}
                                                    {delivery.user.address2 && (
                                                        <div className="truncate max-w-xs">
                                                            住所2: {delivery.user.address2}
                                                        </div>
                                                    )}
                                                    {delivery.user.destination && (
                                                        <div className="truncate max-w-xs">
                                                            名前: {delivery.user.destination}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <div className="space-y-2">
                                                    {delivery.activity.status === 'waiting' && (
                                                        <>
                                                            <div>
                                                                <textarea
                                                                    value={comments.get(delivery.activity.id) || ''}
                                                                    onChange={(e) => handleCommentChange(delivery.activity.id, e.target.value)}
                                                                    placeholder="配送に関するコメントを入力"
                                                                    className="w-full px-3 py-2 border rounded-md"
                                                                    rows={2}
                                                                />
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    const comment = comments.get(delivery.activity.id) || '';
                                                                    markAsDelivered(delivery.activity.id, comment);
                                                                    setComments(prev => {
                                                                        const next = new Map(prev);
                                                                        next.delete(delivery.activity.id);
                                                                        return next;
                                                                    });
                                                                }}
                                                                disabled={processingIds.has(delivery.activity.id) || !(comments.get(delivery.activity.id) || '').trim()}
                                                                className={`w-full px-4 py-2 rounded-md ${processingIds.has(delivery.activity.id) || !(comments.get(delivery.activity.id) || '').trim()
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                                                    }`}
                                                            >
                                                                {processingIds.has(delivery.activity.id) ? (
                                                                    <span className="inline-block animate-spin">⌛</span>
                                                                ) : (
                                                                    '発送済みにする'
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                    {delivery.activity.status === 'processed' && delivery.activity.comment && (
                                                        <div className="text-sm text-gray-600">
                                                            <div className="font-medium">配送コメント:</div>
                                                            <div className="mt-1">{delivery.activity.comment}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーション */}
                    {data && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center">
                                <span className="text-sm text-gray-700">
                                    全{data.total}件中 {(currentPage - 1) * 50 + 1}-
                                    {Math.min(currentPage * 50, data.total)}件を表示
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border rounded-md ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    前へ
                                </button>
                                <button
                                    onClick={handleNextPage}
                                    disabled={!data.hasMore}
                                    className={`px-3 py-1 border rounded-md ${!data.hasMore
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    次へ
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardDeliveryPage;